import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import axiosInstance from '../../services/axiosInstance'
import PageHeader from '../../components/common/PageHeader'
import {
  FileText, School, Book, Calendar, Clock,
  AlertTriangle, ArrowRight, MapPin, CheckCircle2
} from 'lucide-react'
import ChecklistPanel from '../../components/student/ChecklistPanel'
import SubmitApplicationModal from '../../components/modals/SubmitApplicationModal'
import './styles.scss'

export default function StudentDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [profile, setProfile] = useState(null)
  const [applicationData, setApplicationData] = useState(null)
  const [checklistItems, setChecklistItems] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmitApplication = async () => {
    try {
      setSubmitting(true)
      const response = await axiosInstance.post('/api/applications/me/submit')
      if (response.data.success) {
        setShowSubmitModal(false)
        fetchDashboardData()
      } else {
        alert(response.data.message || 'Failed to submit application')
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error connecting to server')
    } finally {
      setSubmitting(false)
    }
  }

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const [profRes, appRes] = await Promise.all([
        axiosInstance.get('/api/profiles/me'),
        axiosInstance.get('/api/applications/me')
      ])

      if (profRes.data?.profile) setProfile(profRes.data.profile)

      const app = appRes.data?.data
      setApplicationData(app)

      if (app?.application?.id) {
        const chkRes = await axiosInstance.get(`/api/checklists/application/${app.application.id}`)
        let items = chkRes.data
        if (items.length === 0) {
          const genRes = await axiosInstance.post(`/api/checklists/generate/${app.application.id}`)
          items = genRes.data
        }
        setChecklistItems(items)

        const docRes = await axiosInstance.get(`/api/documents/application/${app.application.id}`)
        setDocuments(docRes.data || [])
      }
    } catch (e) {
      console.error('Failed to fetch dashboard data', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [])

  useEffect(() => {
    if (!loading && profile && applicationData) {
      const isComplete = Boolean(profile.legalFirstName && profile.legalLastName && profile.dateOfBirth && profile.countryOfCitizenship && profile.phone)
      if (!isComplete) {
        navigate('/complete-profile')
      }
    }
  }, [loading, profile, applicationData, navigate])

  if (loading) {
    return (
      <div className="page-container">
        <PageHeader title="Dashboard" subtitle="Welcome to your student portal" />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(37,99,235,0.2)', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    )
  }

  const firstName = profile?.legalFirstName || user?.fullName || 'Student'
  const universityName = applicationData?.universityName || 'University'
  const termName = applicationData?.intakeName || 'Upcoming Term'
  const progName = applicationData?.programName || 'Degree Program'
  const appNumber = applicationData?.application?.applicationNumber || 'APP-XXXXXXX'

  const isProfileComplete = Boolean(profile?.legalFirstName && profile?.legalLastName && profile?.dateOfBirth && profile?.countryOfCitizenship && profile?.phone)

  const nextItem = checklistItems.find(item => {
    if (item.required === false) return false;
    const doc = documents.find(d => d.documentType === item.documentType);
    const isRejected = doc?.humanReviewStatus === 'REJECTED' || doc?.aiInsight?.decisionStatus === 'REJECTED';
    if (isRejected) return true;
    return item.status !== 'Completed' && item.status !== 'Under Review';
  })

  const i20 = applicationData?.i20Profile
  const isVisaApproved = i20?.visaDecisionStatus === 'APPROVED'
  const isPreDepartureDone = i20?.flightTicketStatus === 'BOOKED' && i20?.accommodationStatus === 'SECURED'
  const needsHousingHelp = i20?.accommodationStatus === 'NEEDS_HELP'

  const admittedStatuses = ['ADMITTED', 'I20_READY', 'I20_ISSUED', 'VISA_PROCESS', 'ARRIVAL_READY', 'ENROLLED']
  const isAdmitted = applicationData?.application?.status && admittedStatuses.includes(applicationData.application.status)

  let nextStepTitle = "Complete your profile"
  let nextStepTime = "Takes 5-7 minutes"
  let nextStepRoute = '/complete-profile'

  if (isProfileComplete) {
    if (nextItem) {
      nextStepTitle = nextItem.title
      nextStepTime = "Action required"
      nextStepRoute = `/document-upload/${applicationData.application.id}/${nextItem.documentType}`
    } else if (applicationData?.application?.status === 'APPLICATION_STARTED') {
      nextStepTitle = "Submit Application"
      nextStepTime = `Ready for ${universityName} review`
      nextStepRoute = 'submit'
    } else if (applicationData?.application?.status === 'SUBMITTED' || applicationData?.application?.status === 'UNDER_REVIEW') {
      nextStepTitle = "Application Under Review"
      nextStepTime = `Awaiting ${universityName} decision`
      nextStepRoute = ''
    } else if (isPreDepartureDone) {
      nextStepTitle = "Ready for Arrival!"
      nextStepTime = "We can't wait to see you on campus"
      nextStepRoute = ''
    } else if (needsHousingHelp || isVisaApproved) {
      nextStepTitle = "Pre-Departure Preparation"
      nextStepTime = "Book your travel and housing"
      nextStepRoute = '/visa'
    } else {
      nextStepTitle = "I-20 & Visa Processing"
      nextStepTime = "Continue with I-20 application"
      nextStepRoute = '/visa'
    }
  }

  const requiredChecklistItems = checklistItems.filter(item => item.required !== false)
  const completedRequiredCount = requiredChecklistItems.filter(item => {
    const doc = documents.find(d => d.documentType === item.documentType)
    const isRejected = doc?.humanReviewStatus === 'REJECTED' || doc?.aiInsight?.decisionStatus === 'REJECTED'
    if (isRejected) return false;

    return item.status === 'Completed' || (doc && doc.humanReviewStatus === 'VERIFIED')
  }).length + (isProfileComplete ? 1 : 0)

  const totalRequiredCount = requiredChecklistItems.length + 1
  const progress = totalRequiredCount === 0 ? 0 : Math.round((completedRequiredCount / totalRequiredCount) * 100)

  return (
    <div className="page-container student-dashboard">
      <PageHeader
        title={`Hi, ${firstName} 👋`}
        subtitle={`${universityName} • ${termName} • ${progName}`}
        universityName={universityName}
      />

      <div className="dashboard-grid">
        <div className="dashboard-main">
          {/* Progress Card */}
          <div className="progress-card">
            <h3 className="progress-card__title">Application Progress</h3>
            <div className="progress-card__percent">{progress}%</div>
            <p className="progress-card__subtitle">Reviewing requirements</p>

            <div className="progress-bar-bg">
              <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>

            <hr className="progress-divider" />

            <div className="app-info-list">
              <div className="app-info-item">
                <FileText size={16} /> <span>ID: {appNumber}</span>
              </div>
              <div className="app-info-item">
                <School size={16} /> <span>{universityName}</span>
              </div>
              <div className="app-info-item">
                <Book size={16} className="dim" /> <span className="dim">{progName}</span>
              </div>
              <div className="app-info-item">
                <Calendar size={16} className="dim" /> <span className="dim">{termName}</span>
              </div>
              {applicationData?.documentDeadline && (
                <div className="app-info-item deadline">
                  <Clock size={16} /> <span>Docs Due: {new Date(applicationData.documentDeadline).toLocaleDateString()}</span>
                </div>
              )}
              {applicationData?.i20Deadline && isVisaApproved === false && (
                <div className="app-info-item warning">
                  <AlertTriangle size={16} /> <span>I-20 Deadline: {new Date(applicationData.i20Deadline).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Next Step Card */}
          <div
            className={`next-step-card ${!nextStepRoute ? 'disabled' : ''}`}
            style={nextStepRoute === 'submit' ? { border: '2px solid #10b981' } : {}}
            onClick={() => {
              if (nextStepRoute === 'submit') {
                setShowSubmitModal(true)
              } else if (nextStepRoute) {
                navigate(nextStepRoute)
              }
            }}
          >
            <div>
              <span className="next-step-label">Next Step</span>
              <h3 className="next-step-title">{nextStepTitle}</h3>
              <span className="next-step-time">{nextStepTime}</span>
            </div>
            {nextStepRoute && (
              <div className="next-step-btn">
                <ArrowRight size={20} />
              </div>
            )}
          </div>

          {/* NEW: Checklist Panel directly on Dashboard */}
          {applicationData?.application && (
            <div className="glass-panel" style={{ marginTop: '1.5rem', padding: '1.5rem' }}>
              <ChecklistPanel appContext={applicationData} onRefreshDashboard={fetchDashboardData} />
            </div>
          )}
        </div>

        <div className="dashboard-sidebar">
          {/* Arrival Info */}
          {isAdmitted && applicationData?.universityAddress && (
            <div className="arrival-card">
              <div className="arrival-header">
                <MapPin size={24} color="#2563eb" />
                <h3>Arrival Check-In</h3>
              </div>
              <p>Upon arriving at {universityName}, please proceed directly to the International Student Office to complete your SEVIS check-in.</p>
              <div className="address-box">
                <MapPin size={16} />
                <div>
                  <strong>Check-In Address</strong>
                  <span>{applicationData.universityAddress}</span>
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats or Info */}
          <div className="quick-info-card">
            <h3>Checklist Overview</h3>
            <div className="checklist-stats">
              <div className="stat">
                <CheckCircle2 size={20} color="#10b981" />
                <div>
                  <strong>{completedRequiredCount}</strong>
                  <span>Completed</span>
                </div>
              </div>
              <div className="stat">
                <FileText size={20} color="#64748b" />
                <div>
                  <strong>{totalRequiredCount - completedRequiredCount}</strong>
                  <span>Remaining</span>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      
      <SubmitApplicationModal 
        show={showSubmitModal} 
        onClose={() => setShowSubmitModal(false)} 
        onSubmit={handleSubmitApplication} 
        submitting={submitting} 
      />
    </div>
  )
}
